import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';

export const CANONICAL_ROOT_URL = 'https://ship.rollo.com';
const DEFAULT_META_TITLE = 'Rollo Ship - Free Shipping Software';
const DEFAULT_META_DESCRIPTION =
  'The free shipping software that saves up to 90% off USPS rates and UPS shipping. ' +
  'Link eCommerce stores, print shipping labels, and more. Join for free!';

interface RouteSeoMetadata {
  title: string;
  description: string;
  canonicalUrl: string;
}

const ROUTE_SEO_METADATA: Record<string, RouteSeoMetadata> = {
  '/login': {
    title: 'Rollo Ship Login – Free Multi-Carrier Shipping Software',
    description:
      'Log in to Rollo Ship, the free multi-carrier shipping app for US & Canada. ' +
      'Compare shipping rates, print shipping labels, and unlock Rollo Rewards discounts.',
    canonicalUrl: `${CANONICAL_ROOT_URL}/login`,
  },
  '/signup': {
    title: 'Free Multi-Carrier Shipping Software Sign Up – US & Canada',
    description:
      'Sign up free for Rollo Ship, the multi-carrier shipping app for US & Canada — ' +
      'no monthly fee. Shop rates, print shipping labels, earn Rollo Rewards perks.',
    canonicalUrl: `${CANONICAL_ROOT_URL}/signup`,
  },
  '/privacy-policy': {
    title: 'Privacy Policy – Rollo Ship Multi-Carrier Shipping Software',
    description:
      'Read the Rollo Ship privacy policy: how the free multi-carrier shipping app collects, ' +
      'uses, and protects your data across the US & Canada.',
    canonicalUrl: `${CANONICAL_ROOT_URL}/privacy-policy`,
  },
};

export const NOINDEX_PATHS = [
  '/notificationPreferences',
  '/home',
  '/grouping',
  '/print-and-email',
  '/mailTemplate',
  '/print',
];

export const isNoindexPath = (pathname: string) =>
  NOINDEX_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));
export const hasDedicatedSeoMetadata = (pathname: string) => !!ROUTE_SEO_METADATA[pathname];

const getCurrentPathname = () => (typeof window === 'undefined' ? '' : window.location.pathname);

interface SeoMetadataProps {
  canonicalUrl?: string;
  noindex?: boolean;
  pathname?: string;
}

const SeoMetadata = ({ canonicalUrl, noindex, pathname }: SeoMetadataProps) => {
  const currentPathname = pathname || getCurrentPathname();
  const routeMetadata = ROUTE_SEO_METADATA[currentPathname];
  const metaTitle = routeMetadata?.title || DEFAULT_META_TITLE;
  const metaDescription = routeMetadata?.description || DEFAULT_META_DESCRIPTION;
  const shouldNoindex = noindex || isNoindexPath(currentPathname);
  const canonicalHref =
    canonicalUrl ||
    routeMetadata?.canonicalUrl ||
    (currentPathname === '/' || currentPathname === '/home' ? CANONICAL_ROOT_URL : null);

  useEffect(() => {
    const descriptionTags = Array.from(
      document.head.querySelectorAll<HTMLMetaElement>('meta[name="description"]')
    );
    const [descriptionTag, ...duplicateDescriptionTags] = descriptionTags;
    const targetDescriptionTag = descriptionTag || document.createElement('meta');

    targetDescriptionTag.setAttribute('name', 'description');
    targetDescriptionTag.setAttribute('content', metaDescription);

    if (!descriptionTag) {
      document.head.appendChild(targetDescriptionTag);
    }

    duplicateDescriptionTags.forEach((tag) => tag.parentNode?.removeChild(tag));
  }, [metaDescription]);

  return (
    <Helmet>
      <title>{metaTitle}</title>
      {shouldNoindex && <meta name="robots" content="noindex" />}
      {canonicalHref && <link rel="canonical" href={canonicalHref} />}
    </Helmet>
  );
};

export default SeoMetadata;
